import type { BoardStage } from "app/components/Dashboard/DeliveryBoardColumn";

export const normalizeDragId = (id: unknown): string => String(id);

export const columnDroppableId = (stageId: number | string) =>
  `column-${stageId}`;

export const parseColumnDroppableId = (
  id: string
): number | string | null => {
  if (!id.startsWith("column-")) {
    return null;
  }
  const raw = id.slice("column-".length);
  if (raw === "none") {
    return "none";
  }
  const numeric = Number(raw);
  return Number.isNaN(numeric) ? raw : numeric;
};

export function stageIdsEqual(
  a: number | string | null | undefined,
  b: number | string | null | undefined
): boolean {
  if (a == null || b == null) {
    return a === b;
  }
  return String(a) === String(b);
}

export function cloneBoardStages(stages: BoardStage[]): BoardStage[] {
  return stages.map((stage) => ({
    ...stage,
    items: [...(stage.items ?? [])],
  }));
}

export function findContainerForItem(
  stages: BoardStage[],
  itemId: number | string
): number | string | null {
  const normalizedId = normalizeDragId(itemId);
  for (const stage of stages) {
    const items = stage.items ?? [];
    if (items.some((item) => normalizeDragId(item.id) === normalizedId)) {
      return stage.id;
    }
  }
  return null;
}

export function findContainerForOver(
  stages: BoardStage[],
  overId: number | string
): number | string | null {
  if (typeof overId === "string") {
    const columnId = parseColumnDroppableId(overId);
    if (columnId !== null) {
      return columnId;
    }
  }

  const itemContainer = findContainerForItem(stages, overId);
  if (itemContainer !== null) {
    return itemContainer;
  }

  const directStage = stages.find((stage) =>
    stageIdsEqual(stage.id, overId)
  );
  return directStage?.id ?? null;
}

export function movePickingBetweenStages(
  stages: BoardStage[],
  activeItemId: number | string,
  overId: number | string
): BoardStage[] {
  const activeContainer = findContainerForItem(stages, activeItemId);
  const overContainer = findContainerForOver(stages, overId);

  if (
    activeContainer === null ||
    overContainer === null ||
    stageIdsEqual(activeContainer, overContainer)
  ) {
    return stages;
  }

  const activeStageIndex = stages.findIndex((stage) =>
    stageIdsEqual(stage.id, activeContainer)
  );
  const overStageIndex = stages.findIndex((stage) =>
    stageIdsEqual(stage.id, overContainer)
  );

  if (activeStageIndex < 0 || overStageIndex < 0) {
    return stages;
  }

  const normalizedActiveId = normalizeDragId(activeItemId);
  const activeItems = [...(stages[activeStageIndex].items ?? [])];
  const overItems = [...(stages[overStageIndex].items ?? [])];
  const activeIndex = activeItems.findIndex(
    (item) => normalizeDragId(item.id) === normalizedActiveId
  );

  if (activeIndex < 0) {
    return stages;
  }

  const [movedItem] = activeItems.splice(activeIndex, 1);

  let overIndex = overItems.length;
  const isColumnTarget =
    typeof overId === "string" && overId.startsWith("column-");

  if (!isColumnTarget) {
    const normalizedOverId = normalizeDragId(overId);
    const itemOverIndex = overItems.findIndex(
      (item) => normalizeDragId(item.id) === normalizedOverId
    );
    if (itemOverIndex >= 0) {
      overIndex = itemOverIndex;
    }
  }

  overItems.splice(overIndex, 0, movedItem);

  return stages.map((stage, index) => {
    if (index === activeStageIndex) {
      return { ...stage, items: activeItems };
    }
    if (index === overStageIndex) {
      return { ...stage, items: overItems };
    }
    return stage;
  });
}
